import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../utils/apiConfig';

const UserProfile = ({ user, onClose, onUpdateUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      fetchUserStats();
    }
  }, [user]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      console.log(`Fetching stats for user ID: ${user.id}`);
      
      // Get token from localStorage
      const token = user.token || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch user's game statistics
      const response = await fetch(`${getApiBaseUrl()}/api/user/stats/${user.id}`, { headers });
      console.log('API Response status:', response.status);
      
      if (response.ok) {
        const stats = await response.json();
        console.log('Received stats from API:', stats);
        setUserStats(stats);
      } else {
        console.error('API request failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // Set empty stats instead of random data
        setUserStats({
          totalGamesPlayed: 0,
          averageScore: 0,
          bestScore: 0,
          totalCorrectAnswers: 0,
          totalQuestions: 0,
          favoriteSubject: 'No games played yet',
          weeklyProgress: 0,
          streak: 0
        });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      
      // Set empty stats instead of random data
      setUserStats({
        totalGamesPlayed: 0,
        averageScore: 0,
        bestScore: 0,
        totalCorrectAnswers: 0,
        totalQuestions: 0,
        favoriteSubject: 'No games played yet',
        weeklyProgress: 0,
        streak: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords if changing
    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      if (formData.newPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
      if (!formData.currentPassword) {
        setError('Current password is required to change password');
        return;
      }
    }

    try {
      const updateData = {
        user_id: user.id,
        username: formData.username,
        email: formData.email
      };

      if (formData.newPassword) {
        updateData.current_password = formData.currentPassword;
        updateData.new_password = formData.newPassword;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/user/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Update user data in parent component
        if (onUpdateUser) {
          onUpdateUser({
            ...user,
            username: formData.username,
            email: formData.email
          });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Network error. Please try again.');
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getProgressSuggestions = () => {
    if (!userStats) return [];

    const suggestions = [];
    const accuracy = (userStats.totalCorrectAnswers / userStats.totalQuestions) * 100;

    if (accuracy < 70) {
      suggestions.push({
        icon: '📚',
        title: 'Practice More',
        description: 'Your accuracy is below 70%. Try practicing basic operations daily.',
        action: 'Focus on fundamentals'
      });
    }

    if (userStats.averageScore < 70) {
      suggestions.push({
        icon: '🎯',
        title: 'Improve Speed',
        description: 'Work on solving problems faster to increase your average score.',
        action: 'Try timed practice sessions'
      });
    }

    if (userStats.streak < 5) {
      suggestions.push({
        icon: '🔥',
        title: 'Build Consistency',
        description: 'Try to play games daily to build a longer streak.',
        action: 'Set daily reminders'
      });
    }

    if (userStats.weeklyProgress < 20) {
      suggestions.push({
        icon: '📈',
        title: 'Increase Activity',
        description: 'Play more games this week to improve your progress.',
        action: 'Aim for 30+ games per week'
      });
    }

    if (suggestions.length === 0) {
      suggestions.push({
        icon: '🌟',
        title: 'Excellent Work!',
        description: 'You\'re doing great! Keep up the good work and try advanced levels.',
        action: 'Challenge yourself with harder problems'
      });
    }

    return suggestions;
  };

  if (!user) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto"
      onClick={(e) => {
        // Close modal when clicking outside of content area
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="flex justify-center items-start pt-16 sm:pt-20 pb-4 sm:pb-8 px-2 sm:px-4 min-h-full">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-6xl max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-6rem)] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 sm:p-6 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="w-10 h-10 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-lg sm:text-2xl">
                👤
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-bold">{user.username.split('@')[0].charAt(0).toUpperCase() + user.username.split('@')[0].slice(1)}'s Profile</h2>
                <p className="text-blue-100 text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">{user.email}</p>
                <div className="flex items-center gap-1 sm:gap-2 mt-1 flex-wrap">
                  <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                    {user.role === 3 ? 'Admin' : user.role === 2 ? 'Premium' : 'Free'}
                  </span>
                  {userStats && (
                    <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                      🔥 {userStats.streak} day streak
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all text-lg sm:text-xl"
              title="Close Profile"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          {/* Navigation Tabs */}
          <div className="flex border-b border-gray-200 mb-4 sm:mb-6">
            <button
              onClick={() => setIsEditing(false)}
              className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-base border-b-2 transition-colors ${
                !isEditing
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="hidden sm:inline">📊 Progress & Stats</span>
              <span className="sm:hidden">📊 Stats</span>
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-sm sm:text-base border-b-2 transition-colors ${
                isEditing
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="hidden sm:inline">⚙️ Edit Profile</span>
              <span className="sm:hidden">⚙️ Edit</span>
            </button>
          </div>

          {/* Progress & Stats Tab */}
          {!isEditing && (
            <div className="space-y-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading your progress...</p>
                </div>
              ) : (
                <>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-2 sm:p-4 rounded-lg sm:rounded-xl shadow-sm">
                      <div className="text-xl sm:text-3xl font-bold text-blue-600 mb-1 sm:mb-2">{userStats?.totalGamesPlayed || 0}</div>
                      <div className="text-xs sm:text-sm text-blue-800 font-medium">Games Played</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-2 sm:p-4 rounded-lg sm:rounded-xl shadow-sm">
                      <div className="text-xl sm:text-3xl font-bold text-green-600 mb-1 sm:mb-2">{userStats?.averageScore || 0}%</div>
                      <div className="text-xs sm:text-sm text-green-800 font-medium">Average Score</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-2 sm:p-4 rounded-lg sm:rounded-xl shadow-sm">
                      <div className="text-xl sm:text-3xl font-bold text-purple-600 mb-1 sm:mb-2">{userStats?.bestScore || 0}%</div>
                      <div className="text-xs sm:text-sm text-purple-800 font-medium">Best Score</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-2 sm:p-4 rounded-lg sm:rounded-xl shadow-sm">
                      <div className="text-xl sm:text-3xl font-bold text-orange-600 mb-1 sm:mb-2">{userStats?.weeklyProgress || 0}</div>
                      <div className="text-xs sm:text-sm text-orange-800 font-medium">This Week</div>
                    </div>
                  </div>

                  {/* Progress Bars */}
                  <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                    <div className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200">
                      <div className="flex justify-between text-sm sm:text-base mb-2 sm:mb-3">
                        <span className="font-medium text-gray-800">Accuracy Rate</span>
                        <span className="font-bold text-blue-600">
                          {userStats && userStats.totalQuestions > 0 
                            ? Math.round((userStats.totalCorrectAnswers / userStats.totalQuestions) * 100) 
                            : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                        <div
                          className={`h-2 sm:h-3 rounded-full transition-all duration-500 ${getProgressColor(
                            userStats && userStats.totalQuestions > 0 
                              ? (userStats.totalCorrectAnswers / userStats.totalQuestions) * 100 
                              : 0
                          )}`}
                          style={{ 
                            width: `${userStats && userStats.totalQuestions > 0 
                              ? (userStats.totalCorrectAnswers / userStats.totalQuestions) * 100 
                              : 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="bg-white p-3 sm:p-4 rounded-lg sm:rounded-xl border border-gray-200">
                      <div className="flex justify-between text-sm sm:text-base mb-2 sm:mb-3">
                        <span className="font-medium text-gray-800">Weekly Goal Progress</span>
                        <span className="font-bold text-green-600">{userStats?.weeklyProgress || 0}/30 games</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                        <div
                          className={`h-2 sm:h-3 rounded-full transition-all duration-500 ${getProgressColor((userStats?.weeklyProgress || 0) / 30 * 100)}`}
                          style={{ width: `${Math.min((userStats?.weeklyProgress || 0) / 30 * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-1 lg:grid-cols-2 sm:gap-6">
                    {/* Favorite Subject */}
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                      <h3 className="font-semibold text-base sm:text-lg text-indigo-800 mb-2">📚 Favorite Subject</h3>
                      <p className="text-sm sm:text-base text-indigo-700 font-medium">{userStats?.favoriteSubject || 'Not enough data'}</p>
                    </div>

                    {/* Suggestions */}
                    <div>
                      <h3 className="font-semibold text-base sm:text-lg text-gray-800 mb-3 sm:mb-4">💡 Suggestions to Improve</h3>
                      <div className="space-y-2 sm:space-y-3">
                        {getProgressSuggestions().slice(0, 2).map((suggestion, index) => (
                          <div key={index} className="bg-white border border-gray-200 p-3 sm:p-4 rounded-lg sm:rounded-xl">
                            <div className="flex items-start gap-2 sm:gap-3">
                              <span className="text-lg sm:text-2xl">{suggestion.icon}</span>
                              <div className="flex-1">
                                <h4 className="font-semibold text-sm sm:text-base text-gray-800 mb-1">{suggestion.title}</h4>
                                <p className="text-gray-600 text-xs sm:text-sm mb-2">{suggestion.description}</p>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  {suggestion.action}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Edit Profile Tab */}
          {isEditing && (
            <div>
              <form onSubmit={handleUpdateProfile} className="space-y-4 sm:space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm">
                    {success}
                  </div>
                )}

                <div className="space-y-6 sm:grid sm:grid-cols-1 lg:grid-cols-2 sm:gap-6 sm:space-y-0">
                  {/* Basic Info */}
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="font-semibold text-base sm:text-lg text-gray-800 border-b border-gray-200 pb-2">Basic Information</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Change */}
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="font-semibold text-base sm:text-lg text-gray-800 border-b border-gray-200 pb-2">Change Password (Optional)</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter current password to change"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter new password (min 6 characters)"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="submit"
                    className="w-full sm:flex-1 bg-blue-600 text-white py-3 px-6 text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setError('');
                      setSuccess('');
                      setFormData({
                        username: user.username,
                        email: user.email,
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: ''
                      });
                    }}
                    className="w-full sm:w-auto px-6 py-3 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;